import { Injectable, Injector } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  of,
  throwError,
  forkJoin,
  switchMap,
  OperatorFunction,
} from 'rxjs';
import {
  catchError,
  finalize,
  map,
  retry,
  shareReplay,
  tap,
} from 'rxjs/operators';
import {
  PatientModel,
  PatientSessionModel,
  PatientBehaviorModel,
  PatientTargetModel,
  SkillAreaModel,
  PatientProgramModel,
  PatientPlaylistModel,
  PromptScheduleModel,
  SessionProgressNoteModel,
  PatientReminderModel,
  TargetType,
  PromptCodeModel,
  BehaviorBipInfoModel,
  PatientBipInfoModel,
  BehaviorFunctionModel,
  PatientBehaviorReplacementModel,
  SkillAcquisitionGoalModel,
  BehaviorGoalModel,
  GuardianModel,
  DataTakerStaffDataModel,
  GroupSessionModel,
  TargetNoteMessageModel,
} from '../api/models';
import {
  AccountApiService,
  StaffApiService,
  SupervisorApiService,
} from '../api/services';
import {
  PatientDataFacadeService,
  ProgramsSyncResult,
  TargetsSyncResult,
} from './patient-data-facade.service';
import { AuthService } from '../../auth/auth.service';
import { Router } from '@angular/router';
import { AcademyModeService, SparkModeKey } from './academy-mode.service';
import { GroupSessionService } from './group-session.service';
import { SparkLightLoadService } from './spark-light-load.service';

export type OfflineGroupSessionModel = GroupSessionModel & { isDirty?: boolean };

@Injectable({
  providedIn: 'root',
})
export class OfflineDataService {
  private offlinePatients = new BehaviorSubject<Map<
    number,
    PatientOfflineData
  > | null>(null);
  private offlinePIN = new BehaviorSubject<string | null>(null);
  private isOfflineMode = new BehaviorSubject<boolean>(false);
  private groupSessions = new BehaviorSubject<
    Map<number, OfflineGroupSessionModel>
  >(new Map());

  private dbName = 'arkmedOfflineDB';
  private dbVersion = 1;
  private db: IDBDatabase | null = null;

  private isSyncingOfflineData = false;
  private dateStartedSendingOfflineData = new Date();
  private dateUpdatedOfflineData = new Date();
  private patientDataLoads = new Map<string, Observable<boolean>>();

  // Use private property for lazy injection
  private authServiceInstance: AuthService | null = null;
  private groupSessionServiceInstance: GroupSessionService | null = null;

  private sendingGuardianSignatureKeys: number[] = [];
  private sendingStaffSignatureKeys: number[] = [];

  private get lightLoad(): SparkLightLoadService {
    return this.injector.get(SparkLightLoadService);
  }

  // Create getter for AuthService to avoid circular dependency
  private get authService(): AuthService {
    if (!this.authServiceInstance) {
      this.authServiceInstance = this.injector.get(AuthService);
    }
    return this.authServiceInstance;
  }

  private get groupSessionService(): GroupSessionService {
    if (!this.groupSessionServiceInstance) {
      this.groupSessionServiceInstance = this.injector.get(GroupSessionService);
    }
    return this.groupSessionServiceInstance;
  }
  offlineTimeout: any;
  constructor(
    private staffApiService: StaffApiService,
    private supervisorApiService: SupervisorApiService,
    private accountApiService: AccountApiService,
    private patientDataFacade: PatientDataFacadeService,
    private injector: Injector,
    private router: Router,
    private academyModeService: AcademyModeService
  ) {
    // Initialize IndexedDB
    this.initIndexedDB()
      .then(() => {
        // Load offline PIN from IndexedDB
        this.getDataFromIndexedDB('offlinePIN').then((pin: string | null) => {
          if (pin) {
            this.offlinePIN.next(pin);
          }
        });
        this.getDataFromIndexedDB('groupSessions').then(
          (groups: [number, OfflineGroupSessionModel][] | null) => {
            if (groups) {
              this.groupSessions.next(new Map(groups));
            }
          }
        );

        // Load offline patients data from IndexedDB - optimized approach
        this.loadOfflinePatientsData();
      })
      .catch((error) => {
        console.error('Error initializing IndexedDB', error);
      });

    // Check for network status with debounce to ensure service worker has time to cache

    window.addEventListener('online', () => {
      console.log('online');
      if (this.offlineTimeout) {
        clearTimeout(this.offlineTimeout);
        this.offlineTimeout = null;
      }
    });

    window.addEventListener('offline', () => {
      console.log('offline');
      if (this.offlineTimeout) {
        clearTimeout(this.offlineTimeout);
        this.offlineTimeout = null;
      }
    });
  }

  public checkIsOnline(): Observable<boolean> {
    const testingServeObs = this.accountApiService.Test();

    // Allow slower beta/live responses before forcing offline mode.
    const timeout = setTimeout(() => {
      this.isOfflineMode.next(true);
    }, 30000);

    console.log('Checking if online');
    return testingServeObs.pipe(
      map((result) => {
        console.log('Online');
        clearTimeout(timeout);
        console.log('Setting online mode');
        this.isOfflineMode.next(false);
        return true;
      }),
      catchError((error) => {
        this.isOfflineMode.next(true);
        clearTimeout(timeout);
        return of(false);
      })
    );
  }

  // New method to check and sync offline data on application startup
  public checkAndSyncOfflineDataOnStartup(): void {
    console.log('Checking for offline data to sync on application startup');
    // Only run if we're online and authenticated
    if (!this.authService.isSignedIn()) {
      console.log('Cannot sync: not authenticated');
      return;
    }

    this.checkIsOnline().subscribe((isOnline) => {
      if (isOnline) {
        const patientsMap = this.offlinePatients.getValue();
        const hasPendingGroups = this.getOfflineGroupSessions().some(
          (group) => (group.ID ?? 0) < 0 && !!group.isDirty
        );
        if ((!patientsMap || patientsMap.size === 0) && !hasPendingGroups) {
          console.log('No offline data to sync on startup');
          return;
        }

        let hasDirtyData = hasPendingGroups;

        // Check if there are any dirty sessions, target notes, or progress notes that need to be synced
        patientsMap?.forEach((patientData, patientId) => {
          // Check for dirty sessions
          const hasDirtySessions = patientData.sessions.some(
            (session) => session.isDirty
          );

          // Check for dirty progress notes
          let hasDirtyProgressNotes = false;
          if (patientData.progressNotes) {
            hasDirtyProgressNotes = Object.values(
              patientData.progressNotes
            ).some((note) => (note as any).isDirty);
          }

          const hasPendingTargetNoteMessages =
            this.hasPendingTargetNoteMessages(patientData);

          if (
            hasDirtySessions ||
            hasDirtyProgressNotes ||
            hasPendingTargetNoteMessages
          ) {
            console.log(
              `Patient ${patientId} has dirty data that needs syncing on startup`
            );
            hasDirtyData = true;
          }
        });

        if (hasDirtyData) {
          console.log('Syncing offline data on application startup');
          this.syncOfflineDataWithGroupReconciliation().subscribe({
            next: () => {
              console.log(
                'Successfully synced offline data on application startup'
              );
            },
            error: (error) => {
              console.error(
                'Error syncing offline data on application startup:',
                error
              );
            },
          });
        } else {
          console.log('No dirty data to sync on application startup');
        }
      } else {
        console.log('Cannot sync: not online');
      }
    });
  }

  // Initialize IndexedDB
  private initIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve();
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = (event) => {
        console.error('IndexedDB error:', event);
        reject('IndexedDB error');
      };

      request.onsuccess = (event) => {
        this.db = (event.target as IDBOpenDBRequest).result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains('offlineData')) {
          db.createObjectStore('offlineData', { keyPath: 'key' });
        }
      };
    });
  }

  // Save data to IndexedDB
  private saveDataToIndexedDB(key: string, value: any): Promise<void> {
    return this.saveEntriesToIndexedDB([{ key, value }]);
  }

  private saveEntriesToIndexedDB(
    entries: Array<{ key: string; value: any }>
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject('IndexedDB not initialized');
        return;
      }

      if (entries.length === 0) {
        resolve();
        return;
      }

      const transaction = this.db.transaction(['offlineData'], 'readwrite');
      const store = transaction.objectStore('offlineData');

      transaction.oncomplete = () => resolve();
      transaction.onerror = (event) => {
        console.error('Error saving to IndexedDB:', event);
        reject('Error saving to IndexedDB');
      };
      transaction.onabort = () => reject('IndexedDB transaction aborted');

      for (const entry of entries) {
        store.put({ key: entry.key, value: entry.value });
      }
    });
  }

  // Get data from IndexedDB
  private getDataFromIndexedDB(key: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject('IndexedDB not initialized');
        return;
      }

      const transaction = this.db.transaction(['offlineData'], 'readonly');
      const store = transaction.objectStore('offlineData');

      const request = store.get(key);

      request.onsuccess = () => {
        resolve(request.result ? request.result.value : null);
      };

      request.onerror = (event) => {
        console.error('Error getting data from IndexedDB:', event);
        reject('Error getting data from IndexedDB');
      };
    });
  }

  private getAllGuardianSgnatureKeys(): Promise<string[]> {
    return this.getAllKeysByPrefix('offlineSessionGuardianSignature');
  }

  private getAllStaffSignatureKeys(): Promise<string[]> {
    return this.getAllKeysByPrefix('offlineSessionStaffHandoffSignature');
  }

  private getAllKeysByPrefix(prefix: string): Promise<string[]> {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject('IndexedDB not initialized');
        return;
      }
      const transaction = this.db?.transaction(['offlineData'], 'readonly');
      const store = transaction?.objectStore('offlineData');

      const request = store?.getAllKeys();
      request.onsuccess = () => {
        resolve(
          request?.result
            ? (request?.result as string[]).filter((key) =>
                key.startsWith(prefix)
              )
            : []
        );
      };
      request.onerror = (event) => {
        console.error(
          'Error getting guardian signature keys from IndexedDB:',
          event
        );
        reject('Error getting signature keys from IndexedDB');
      };
    });
  }

  private removeDataFromIndexedDB(key: string): void {
    this.db
      ?.transaction(['offlineData'], 'readwrite')
      .objectStore('offlineData')
      .delete(key);
  }

  // Get offline mode status
  public getOfflineModeStatus(): Observable<boolean> {
    return this.isOfflineMode.asObservable();
  }

  // Get current offline mode
  public isInOfflineMode(): boolean {
    return this.isOfflineMode.getValue();
  }

  // Get offline PIN
  public getOfflinePIN(): Observable<string | null> {
    return this.offlinePIN.asObservable();
  }

  // Set offline PIN
  public setOfflinePIN(pin: string): void {
    this.saveDataToIndexedDB('offlinePIN', pin).catch((error) => {
      console.error('Error saving PIN to IndexedDB:', error);
    });
    this.offlinePIN.next(pin);
  }

  // Verify offline PIN
  public verifyOfflinePIN(enteredPin: string): boolean {
    return enteredPin === this.offlinePIN.getValue();
  }

  // Get all offline patients
  public getOfflinePatients(): Observable<Map<
    number,
    PatientOfflineData
  > | null> {
    return this.offlinePatients.asObservable();
  }

  // Check if patient data exists offline
  public hasOfflinePatientData(patientId: number): boolean {
    return !!this.getOfflinePatientData(patientId);
  }

  // Get specific patient offline data
  public getOfflinePatientData(patientId: number): PatientOfflineData | null {
    const patientData =
      this.offlinePatients.getValue()?.get(Number(patientId)) || null;
    if (!patientData) {
      return null;
    }
    if (Number(patientData.patient?.ID) !== Number(patientId)) {
      console.error('Rejected cross-patient offline cache read', {
        patientId,
        dataPatientId: patientData.patient?.ID,
      });
      return null;
    }

    return this.isOfflineDataForCurrentMode(patientData) ? patientData : null;
  }

  public hasUnsyncedPatientData(patientId: number): boolean {
    const patientData = this.getOfflinePatientData(patientId);
    if (!patientData) {
      return false;
    }

    const hasUnsyncedSessions = patientData.sessions.some((session) =>
      this.hasLikelyUnsyncedSessionData(session)
    );
    if (hasUnsyncedSessions) {
      return true;
    }

    const hasUnsyncedProgressNotes = patientData.progressNotes
      ? Object.values(patientData.progressNotes).some(
          (note) => (note as any).isDirty
        )
      : false;

    return (
      hasUnsyncedProgressNotes ||
      this.hasPendingTargetNoteMessages(patientData)
    );
  }

  private hasPendingTargetNoteMessages(patientData: PatientOfflineData): boolean {
    if (!patientData.targetNoteMessages) {
      return false;
    }

    return Object.values(patientData.targetNoteMessages).some((messages) =>
      messages.some(
        (message) =>
          message.SyncStatus === 'pending' || message.SyncStatus === 'failed'
      )
    );
  }

  /**
   * Detect whether a local session likely contains unsynced changes.
   * We intentionally keep this conservative to avoid overwriting local data.
   */
  private hasLikelyUnsyncedSessionData(
    session: PatientSessionModel & { isDirty?: boolean }
  ): boolean {
    // Session -1 is a local preview placeholder and should never participate in
    // unsynced-data preservation/sync logic for real sessions.
    if (!session.ID || session.ID <= 0) {
      return false;
    }

    if (session.isDirty) {
      return true;
    }

    const hasUnsyncedTargetData =
      session.TargetData?.some((x) => !x.ID || x.ID <= 0) ?? false;
    if (hasUnsyncedTargetData) {
      return true;
    }

    const hasUnsyncedBehaviorData =
      session.BehaviorData?.some((x) => !x.ID || x.ID <= 0) ?? false;
    if (hasUnsyncedBehaviorData) {
      return true;
    }

    return false;
  }

  /**
   * Returns JSON string of all offline data that has not been synced yet (dirty sessions, dirty progress notes, pending target notes).
   * Used for Send logs / recovery. May be truncated by backend (7000 chars).
   * Patient and guardian/staff names are redacted so they are not exposed in logs.
   */
  public getUnsyncedOfflineDataJson(): string {
    const map = this.offlinePatients.getValue();
    if (!map || map.size === 0) return '{}';
    try {
      const unsynced: { unsyncedAt: string; patients: unknown[] } = {
        unsyncedAt: new Date().toISOString(),
        patients: [],
      };
      const redacted = '[Redacted]';
      map.forEach((data, patientId) => {
        const hasDirtySessions =
          data.sessions?.some((s) => (s as { isDirty?: boolean }).isDirty) ??
          false;
        const hasDirtyNotes =
          data.progressNotes &&
          Object.values(data.progressNotes).some(
            (n: unknown) => (n as { isDirty?: boolean })?.isDirty
          );
        const pendingTargetNotes = data.targetNoteMessages
          ? Object.fromEntries(
              Object.entries(data.targetNoteMessages)
                .map(([targetId, messages]) => [
                  targetId,
                  messages.filter(
                    (message) =>
                      message.SyncStatus === 'pending' ||
                      message.SyncStatus === 'failed'
                  ),
                ])
                .filter(([, messages]) => messages.length > 0)
            )
          : {};
        const hasPendingTargetNotes = Object.keys(pendingTargetNotes).length > 0;
        if (!hasDirtySessions && !hasDirtyNotes && !hasPendingTargetNotes) return;
        const dirtySessions =
          data.sessions?.filter((s) => (s as { isDirty?: boolean }).isDirty) ??
          [];
        const sessionsRedacted = dirtySessions.map((s) => {
          const session = s as Record<string, unknown>;
          return {
            ...session
          };
        });
        unsynced.patients.push({
          patientId,
          patientName: redacted,
          sessions: sessionsRedacted,
          progressNotes: data.progressNotes
            ? Object.fromEntries(
                Object.entries(data.progressNotes).filter(
                  ([, n]) => (n as { isDirty?: boolean })?.isDirty
                )
              )
            : {},
          targetNoteMessages: pendingTargetNotes,
        });
      });
      return JSON.stringify(unsynced);
    } catch (e) {
      return JSON.stringify({
        error: 'Failed to serialize unsynced data',
        message: String(e),
      });
    }
  }

  /**
   * Unsynced sessions and progress notes only (no full patient profile).
   * Used by the native Opto App encrypted sandbox backup.
   */
  public getNativeBackupJson(): string {
    const map = this.offlinePatients.getValue();
    const payload = {
      version: 2,
      ok: true,
      mapReady: !!map,
      backedUpAt: new Date().toISOString(),
      patients: [] as {
        patientId: number;
        sessions: (PatientSessionModel & { isDirty?: boolean })[];
        progressNotes: { [sessionId: number]: SessionProgressNoteModel };
      }[],
    };

    if (!map) {
      return JSON.stringify(payload);
    }

    try {
      map.forEach((data, patientId) => {
        if (!this.hasUnsyncedPatientData(patientId)) {
          return;
        }
        payload.patients.push({
          patientId,
          sessions:
            data.sessions?.filter((session) =>
              this.hasLikelyUnsyncedSessionData(session)
            ) ?? [],
          progressNotes: data.progressNotes
            ? Object.fromEntries(
                Object.entries(data.progressNotes).filter(
                  ([, note]) => (note as { isDirty?: boolean })?.isDirty
                )
              )
            : {},
        });
      });
      return JSON.stringify(payload);
    } catch (e) {
      return JSON.stringify({
        version: 2,
        ok: false,
        mapReady: true,
        backedUpAt: new Date().toISOString(),
        patients: [],
        error: String(e),
      });
    }
  }

  /**
   * Merge a native sandbox backup into IndexedDB. Only unsynced sessions/notes
   * are applied, and only onto a patient that already exists offline.
   * Prefers in-web dirty data over the snapshot.
   */
  public async importNativeBackup(
    json: string
  ): Promise<{ ok: boolean; restored: number; skipped: number; errors: string[] }> {
    const result = {
      ok: true,
      restored: 0,
      skipped: 0,
      errors: [] as string[],
    };

    let parsed: {
      patients?: {
        patientId: number;
        sessions?: (PatientSessionModel & { isDirty?: boolean })[];
        progressNotes?: { [sessionId: number]: SessionProgressNoteModel };
        data?: PatientOfflineData;
      }[];
    };
    try {
      parsed = JSON.parse(json);
    } catch (e) {
      return {
        ok: false,
        restored: 0,
        skipped: 0,
        errors: [`Invalid backup JSON: ${String(e)}`],
      };
    }

    const incoming = parsed.patients ?? [];
    if (incoming.length === 0) {
      return result;
    }

    await this.initIndexedDB().catch(() => undefined);
    const map = await this.waitForPatientsMap();
    if (!map) {
      result.ok = false;
      result.errors.push('Offline patient map is not ready');
      return result;
    }

    for (const entry of incoming) {
      const patientId = Number(entry.patientId);
      const backupSessions = entry.sessions ?? entry.data?.sessions ?? [];
      const backupNotes = entry.progressNotes ?? entry.data?.progressNotes ?? {};
      if (!patientId) {
        result.skipped += 1;
        continue;
      }

      try {
        const existing = map.get(patientId);
        if (!existing) {
          // Do not recreate a patient from sessions/notes alone.
          result.skipped += 1;
          continue;
        }

        existing.sessions = this.mergeSessionsPreferringExistingDirty(
          existing.sessions || [],
          backupSessions
        );
        existing.progressNotes = this.mergeProgressNotesPreferringExistingDirty(
          existing.progressNotes || {},
          backupNotes
        );
        await this.updateOfflinePatientData(patientId, existing, false);
        result.restored += 1;
      } catch (e) {
        result.errors.push(`patient ${patientId}: ${String(e)}`);
        result.ok = false;
      }
    }

    this.offlinePatients.next(new Map(map));
    return result;
  }

  private async waitForPatientsMap(
    timeoutMs = 8000
  ): Promise<Map<number, PatientOfflineData> | null> {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      const map = this.offlinePatients.getValue();
      if (map) {
        return map;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return this.offlinePatients.getValue();
  }

  private mergeSessionsPreferringExistingDirty(
    existing: (PatientSessionModel & { isDirty?: boolean })[],
    backup: (PatientSessionModel & { isDirty?: boolean })[]
  ): (PatientSessionModel & { isDirty?: boolean })[] {
    const byId = new Map<number, PatientSessionModel & { isDirty?: boolean }>();
    existing.forEach((session) => {
      if (session.ID && session.ID > 0) {
        byId.set(session.ID, session);
      }
    });
    backup.forEach((session) => {
      if (!session.ID || session.ID <= 0) {
        return;
      }
      const current = byId.get(session.ID);
      if (!current) {
        byId.set(session.ID, session);
        return;
      }
      if (current.isDirty) {
        return;
      }
      if (session.isDirty) {
        byId.set(session.ID, session);
      }
    });
    return Array.from(byId.values());
  }

  private mergeProgressNotesPreferringExistingDirty(
    existing: { [sessionId: number]: SessionProgressNoteModel },
    backup: { [sessionId: number]: SessionProgressNoteModel }
  ): { [sessionId: number]: SessionProgressNoteModel } {
    const merged = { ...existing };
    Object.entries(backup).forEach(([key, note]) => {
      const sessionId = Number(key);
      const current = merged[sessionId] as
        | (SessionProgressNoteModel & { isDirty?: boolean })
        | undefined;
      const incoming = note as SessionProgressNoteModel & { isDirty?: boolean };
      if (!current) {
        merged[sessionId] = note;
        return;
      }
      if ((current as { isDirty?: boolean }).isDirty) {
        return;
      }
      if (incoming?.isDirty) {
        merged[sessionId] = note;
      }
    });
    return merged;
  }

  // The session cache always takes a full snapshot. A session cannot afford a cursor that skips a
  // target, and this cache is the one the session screen reads from while data is being collected.
  private fetchTargetsForOffline(
    patientId: number,
    onlyActive: boolean
  ): Observable<TargetsSyncResult> {
    return this.patientDataFacade.syncTargets(
      patientId,
      onlyActive,
      [],
      null,
      true,
      true
    );
  }

  private fetchProgramsForOffline(
    patientId: number,
    onlyActive: boolean
  ): Observable<ProgramsSyncResult> {
    return this.patientDataFacade.syncPrograms(
      patientId,
      onlyActive,
      [],
      null,
      true,
      true
    );
  }

  // Save patient data for offline use
  public savePatientForOffline(
    patientId: number,
    includeSessionHistory: boolean = true,
    sessionIdToInclude?: number
  ): Observable<boolean> {
    const modeKey = this.academyModeService.getModeKey();
    const loadKey = `${modeKey}:${patientId}:${includeSessionHistory}:${
      sessionIdToInclude ?? ''
    }`;
    const existingLoad = this.patientDataLoads.get(loadKey);
    if (existingLoad) {
      return existingLoad;
    }

    // If we're already offline, we can't fetch new data
    if (this.isInOfflineMode()) {
      return throwError(
        () => new Error('Cannot download patient data while offline')
      );
    }

    // Try syncing pending offline writes first, but do not block fresh read-data
    // loads when upload/sync fails.
    const patientDataLoad = this.syncOfflineDataWithGroupReconciliation().pipe(
      catchError((error) => {
        console.error(
          'Error syncing offline data before fetching patient data',
          error
        );
        return of(false);
      }),
      switchMap(() =>
        this.savePatientForOfflineAnfterSync(
          patientId,
          includeSessionHistory,
          sessionIdToInclude
        )
      ),
      catchError((error) => {
        console.error('Error fetching patient data for offline cache', error);
        return of(false);
      }),
      finalize(() => {
        if (this.patientDataLoads.get(loadKey) === patientDataLoad) {
          this.patientDataLoads.delete(loadKey);
        }
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.patientDataLoads.set(loadKey, patientDataLoad);
    return patientDataLoad;
  }

  public savePatientForOfflineAnfterSync(
    patientId: number,
    includeSessionHistory: boolean = true,
    sessionIdToInclude?: number
  ): Observable<boolean> {
    return this.staffApiService.GetPatientInfo({ id: patientId }).pipe(
      switchMap((patient) => {
        // Function to wrap observables with error handling
        const handleError = <T, R>(obs$: Observable<T>) =>
          obs$.pipe(
            catchError((error) => {
              console.error('Observable error:', error);
              return of(null as T | null);
            })
          );

        const sessionsObservable = includeSessionHistory
          ? handleError(
              this.staffApiService.GetPatientSessions({ patientID: patientId })
            )
          : sessionIdToInclude && sessionIdToInclude > 0
            ? handleError(
                this.staffApiService
                  .GetPatientSessionWithData({ sessionID: sessionIdToInclude })
                  .pipe(map((session) => (session ? [session] : [])))
              )
            : of([]);

        const skipHeavy = this.lightLoad.isEnabled();

        // Split API calls into smaller batches to reduce memory spikes
        // Batch 1: Core patient data. Staff snapshots always omit program/target/playlist
        // HTML so visual supports are not in the collection path.
        const coreDataBatch = forkJoin({
          behaviors: handleError(
            this.staffApiService.GetPatientBehaviors({
              patientID: patientId,
              onlyActive: true,
              lightLoad: true,
            })
          ),
          targets: handleError(
            this.fetchTargetsForOffline(patientId, true)
          ),
          sessions: sessionsObservable,
          programs: handleError(
            this.fetchProgramsForOffline(patientId, true)
          ),
          playlists: handleError(
            this.supervisorApiService.GetPatientPlaylists({
              patientID: patientId,
              onlyActive: true,
              lightLoad: true,
            })
          ),
        });

        // Batch 2: Behavior-related data
        const behaviorDataBatch = forkJoin({
          behaviorLocations: handleError(
            this.staffApiService.GetPatientBehaviorLocations({
              onlyActive: true,
            })
          ),
          behaviorConsequences: handleError(
            this.staffApiService.GetPatientBehaviorConsequences({
              patientID: patientId,
              onlyActive: true,
            })
          ),
          behaviorResponses: handleError(
            this.staffApiService.GetPatientBehaviorResponses({
              onlyActive: true,
            })
          ),
          behaviorSeverities: handleError(
            this.staffApiService.GetPatientBehaviorSeverities({
              onlyActive: true,
            })
          ),
          behaviorAntecedents: handleError(
            this.staffApiService.GetPatientBehaviorAntecedents({
              patientID: patientId,
              onlyActive: true,
            })
          ),
          behaviorReplacements: handleError(
            this.staffApiService.GetPatientBehaviorReplacements({
              patientID: patientId,
              onlyActive: true,
            })
          ),
        });

        // Batch 3: Additional data
        const additionalDataBatch = forkJoin({
          skillAreas: handleError(
            this.supervisorApiService.GetSkillAreas({
              patientID: patientId,
              onlyActive: true,
            })
          ),
          promptSchedules: handleError(
            this.supervisorApiService.GetPromptSchedules({
              patientID: patientId,
              onlyActive: true,
            })
          ),
          genericPromptCodes: handleError(
            this.supervisorApiService.GetPromptCodes({})
          ),
          skillAcquisitionGoals: skipHeavy
            ? of(null)
            : handleError(
            this.supervisorApiService.GetPatientSkillAcquisitionGoals({
              patientID: patientId,
            })
          ),
          behaviorGoals: skipHeavy
            ? of(null)
            : handleError(
            this.supervisorApiService.GetPatientBehaviorGoals({
              patientID: patientId,
            })
          ),
          reminders: handleError(
            this.supervisorApiService.GetPatientReminders({
              patientID: patientId,
            })
          ),
          patientInstructions: of(null),
          bipInterventions: of(null),
          currentPatientBipInfo: of(null),
          currentBehaviorBipInfos: of(null),
          behaviorFunctions: skipHeavy
            ? of(null)
            : handleError(
          this.staffApiService.GetBehaviorFunctions()
          ),
          guardians: handleError(
            this.staffApiService.GetPatientGuardians({
              patientID: patientId,
            })
          ),
          activeStaffMembers: handleError(
            this.staffApiService.GetActiveStaffMembers().pipe(retry(2))
          ),
        });

        // Process batches sequentially to reduce memory load
        return coreDataBatch.pipe(
          switchMap((coreResult) => {
            return behaviorDataBatch.pipe(
              switchMap((behaviorResult) => {
                return additionalDataBatch.pipe(
                  map((additionalResult) => ({
                    ...coreResult,
                    ...behaviorResult,
                    ...additionalResult,
                  }))
                );
              })
            );
          }),
          switchMap((result) => {
            // Get current offline data for this patient (if any) to preserve local changes
            const currentOfflineData = this.getOfflinePatientData(patientId);
            const offlineData: PatientOfflineData = {
              sparkModeKey: this.academyModeService.getModeKey(),
              isAcademyMode: this.academyModeService.isAcademyModeEnabled(),
              patient: patient,
              behaviors: [],
              targets: [],
              sessions: [],
              finishedSessions: currentOfflineData?.finishedSessions || [],
              behaviorLocations: [],
              behaviorConsequences: [],
              behaviorResponses: [],
              behaviorSeverities: [],
              behaviorAntecedents: [],
              behaviorReplacements: [],
              skillAreas: [],
              programs: [],
              playlists: [],
              promptSchedules: [],
              genericPromptCodes: [],
              skillAcquisitionGoals: [],
              behaviorGoals: [],
              reminders: [],
              progressNotes: currentOfflineData?.progressNotes || {},
              targetNoteMessages: currentOfflineData?.targetNoteMessages || {},
              patientInstructions: '',
              bipInterventions: [],
              patientBipInfo: null,
              behaviorBipInfos: [],
              behaviorFunctions: skipHeavy
                ? []
                : currentOfflineData?.behaviorFunctions || [],
              guardians: currentOfflineData?.guardians || [],
              activeStaffMembers: currentOfflineData?.activeStaffMembers || [],
              programsDeltaCursor: currentOfflineData?.programsDeltaCursor || null,
              targetsDeltaCursor: currentOfflineData?.targetsDeltaCursor || null,
              lastSyncTime: new Date(),
            };
            // Update the offline data with all fetched data
            if (result.behaviors) {
              offlineData.behaviors = this.lightLoad.stripBehaviors(result.behaviors);
            }
            if (result.targets) {
              offlineData.targets = result.targets.targets;
              // Sessions never resume from a cursor, so any cursor left by an older build is dropped
              // rather than kept where a later sync could resume from it.
              offlineData.targetsDeltaCursor = null;
            }

            if (result.genericPromptCodes) {
              offlineData.genericPromptCodes = result.genericPromptCodes;
            }

            // Merge server sessions with local unsynced sessions
            if (result.sessions) {
              const serverSessions = includeSessionHistory
                ? result.sessions.filter((s) => !s.FinishedDate)
                : result.sessions;
              // Get current offline data for this patient (if any) to preserve local changes
              const currentOfflineData = this.getOfflinePatientData(patientId);

              if (currentOfflineData) {
                // Keep any local sessions that are likely unsynced, even if a previous flow accidentally cleared isDirty.
                const localSessionsToPreserve = currentOfflineData.sessions.filter(
                  (localSession) => this.hasLikelyUnsyncedSessionData(localSession)
                );

                // Keep local unsynced sessions and add the remaining server sessions.
                if (localSessionsToPreserve.length > 0) {
                  // Get IDs of local sessions we are preserving
                  const preservedSessionIds = localSessionsToPreserve
                    .map((s) => s.ID)
                    .filter((id): id is number => !!id);

                  // Keep server sessions that don't have local preserved version
                  const serverSessionsFiltered = serverSessions.filter(
                    (serverSession) =>
                      !serverSession.ID ||
                      !preservedSessionIds.includes(serverSession.ID)
                  );

                  // Combine filtered server sessions with preserved local sessions
                  offlineData.sessions = [
                    ...localSessionsToPreserve,
                    ...serverSessionsFiltered,
                  ];
                } else {
                  offlineData.sessions = serverSessions;
                }
              } else {
                offlineData.sessions = serverSessions;
              }
            }

            if (result.behaviorLocations) {
              offlineData.behaviorLocations = result.behaviorLocations;
            }
            if (result.behaviorConsequences) {
              offlineData.behaviorConsequences = result.behaviorConsequences;
            }
            if (result.behaviorResponses) {
              offlineData.behaviorResponses = result.behaviorResponses;
            }
            if (result.behaviorSeverities) {
              offlineData.behaviorSeverities = result.behaviorSeverities;
            }
            if (result.behaviorAntecedents) {
              offlineData.behaviorAntecedents = result.behaviorAntecedents;
            }
            if (result.behaviorReplacements) {
              offlineData.behaviorReplacements =
                result.behaviorReplacements as unknown as PatientBehaviorReplacementModel[];
            }
            if (result.skillAreas) {
              offlineData.skillAreas = result.skillAreas;
            }
            if (result.programs) {
              offlineData.programs = result.programs.programs;
              offlineData.programsDeltaCursor = null;
            }
            if (result.playlists) {
              offlineData.playlists = this.lightLoad.stripPlaylists(result.playlists);
            }
            if (result.promptSchedules) {
              offlineData.promptSchedules = result.promptSchedules;
            }
            if (result.skillAcquisitionGoals) {
              offlineData.skillAcquisitionGoals = result.skillAcquisitionGoals;
            }
            if (result.behaviorGoals) {
              offlineData.behaviorGoals = result.behaviorGoals;
            }
            if (result.reminders) {
              offlineData.reminders = result.reminders;
            }
            if (result.behaviorFunctions) {
              offlineData.behaviorFunctions = result.behaviorFunctions;
            }
            if (result.guardians) {
              offlineData.guardians = result.guardians;
            }
            if (result.activeStaffMembers) {
              offlineData.activeStaffMembers = result.activeStaffMembers;
            }

            if (this.lightLoad.isEnabled()) {
              this.stripHeavyOfflineFields(offlineData);
            }

            this.updateOfflinePatientData(patientId, offlineData);

            if (!this.lightLoad.isEnabled()) {
              this.lightLoad.clearForceFullSnapshot();
            }

            // Don't load all progress notes upfront - load them lazily when needed
            // This significantly reduces memory usage during initialization

            // If we don't have a PIN yet, generate one
            if (!this.offlinePIN.getValue()) {
              this.generateAndStoreOfflinePIN();
            }

            return of(true);
          }),
          catchError((error) => {
            console.error('Error saving patient offline data', error);
            return of(false);
          })
        );
      }),
      catchError((error) => {
        console.error(
          'Error syncing offline data before fetching patient data',
          error
        );
        return of(false);
      })
    );
  }

  // Add or update session data in offline storage
  public saveOfflineSessionData(
    patientId: number,
    session: PatientSessionModel,
    isDirty: boolean = true,
    syncImmediately: boolean = true
  ): Promise<boolean | undefined> {
    if (
      session.PatientID != null &&
      Number(session.PatientID) !== Number(patientId)
    ) {
      throw new Error(
        `Session patient ${session.PatientID} does not match cache patient ${patientId}`
      );
    }
    const patientData = this.getOfflinePatientData(patientId);
    if (!patientData) {
      throw new Error('Patient data not found offline');
    }

    // Keep preview-only placeholder sessions out of persisted lists. Negative
    // IDs are reserved for offline-created group members until reconciliation.
    if (!session.ID || (session.ID <= 0 && !session.GroupSessionID)) {
      return Promise.resolve(true);
    }

    // Find if session already exists
    const existingSessionIndex = patientData.sessions.findIndex(
      (s) => s.ID === session.ID
    );
    const existingSession =
      existingSessionIndex !== -1
        ? patientData.sessions[existingSessionIndex]
        : null;

    // Never downgrade a known-dirty session back to clean outside sync completion.
    const effectiveDirty =
      isDirty ||
      !!existingSession?.isDirty ||
      this.hasLikelyUnsyncedSessionData(
        session as PatientSessionModel & { isDirty?: boolean }
      );

    // Always persist the session with explicit dirty state.
    const modifiedSession = Object.assign({}, session, {
      isDirty: effectiveDirty,
    });

    if (existingSessionIndex !== -1) {
      // Update existing session
      patientData.sessions[existingSessionIndex] = modifiedSession;
    } else {
      // Add new session
      patientData.sessions.push(modifiedSession);
    }

    // Sync only when explicitly requested to avoid blocking critical UI actions
    // like starting a session.
    const currentMap = this.offlinePatients.getValue();
    if (!currentMap) {
      return Promise.resolve(false);
    }
    currentMap.set(patientId, patientData);
    this.offlinePatients.next(new Map(currentMap));
    this.dateUpdatedOfflineData = new Date();

    return this.persistPatientSessions(patientId, patientData)
      .then(() => {
        this.notifyNativeUnsyncedChanged();
        return true;
      })
      .catch(() => {
        return false;
      });
  }

  public getOfflineGroupSession(
    groupSessionID: number
  ): OfflineGroupSessionModel | null {
    return this.groupSessions.getValue().get(groupSessionID) ?? null;
  }

  public getOfflineGroupSessions(): OfflineGroupSessionModel[] {
    return Array.from(this.groupSessions.getValue().values());
  }

  public getOfflineGroupSessionsObservable(): Observable<
    Map<number, OfflineGroupSessionModel>
  > {
    return this.groupSessions.asObservable();
  }

  public async saveOfflineGroupSession(
    groupSession: OfflineGroupSessionModel
  ): Promise<void> {
    const groupID = groupSession.ID;
    if (groupID == null) {
      throw new Error('Group session is missing an ID');
    }
    const groups = new Map(this.groupSessions.getValue());
    groups.set(groupID, groupSession);
    this.groupSessions.next(groups);
    await this.saveDataToIndexedDB(
      'groupSessions',
      Array.from(groups.entries())
    );
  }

  public async replaceOfflineGroupSession(
    temporaryGroupID: number,
    groupSession: OfflineGroupSessionModel
  ): Promise<void> {
    const groupID = groupSession.ID;
    if (groupID == null) {
      throw new Error('Group session is missing an ID');
    }
    const groups = new Map(this.groupSessions.getValue());
    groups.delete(temporaryGroupID);
    groups.set(groupID, groupSession);
    this.groupSessions.next(groups);
    await this.saveDataToIndexedDB(
      'groupSessions',
      Array.from(groups.entries())
    );
  }

  public updateOfflineSessionFromServer(
    patientId: number,
    session: PatientSessionModel,
    isDirty: boolean = false
  ): Promise<boolean | undefined> {
    const patientData = this.getOfflinePatientData(patientId);
    if (!patientData) {
      return Promise.resolve(false);
    }

    if (!session.ID || session.ID <= 0) {
      return Promise.resolve(false);
    }

    const sessionFromServer = { ...session, isDirty };
    const existingSessionIndex = patientData.sessions.findIndex(
      (existingSession) => existingSession.ID === session.ID
    );

    if (existingSessionIndex >= 0) {
      patientData.sessions[existingSessionIndex] = sessionFromServer;
    } else {
      patientData.sessions.push(sessionFromServer);
    }

    return this.persistPatientSessionsOnly(patientId, patientData);
  }

  public removeOfflineSessionData(
    patientId: number,
    sessionID: number
  ): Promise<boolean | undefined> {
    const patientData = this.getOfflinePatientData(patientId);
    if (!patientData) {
      return Promise.resolve(false);
    }
    patientData.sessions = patientData.sessions.filter(
      (session) => session.ID !== sessionID
    );
    return this.persistPatientSessionsOnly(patientId, patientData);
  }

  public async rekeyOfflineSessionArtifacts(
    patientId: number,
    oldSessionID: number,
    newSessionID: number
  ): Promise<void> {
    const patientData = this.getOfflinePatientData(patientId);
    const progressNote = patientData?.progressNotes?.[oldSessionID];
    if (patientData && progressNote) {
      delete patientData.progressNotes[oldSessionID];
      (progressNote as SessionProgressNoteModel & {
        PatientSessionID?: number;
      }).PatientSessionID = newSessionID;
      patientData.progressNotes[newSessionID] = progressNote;
      await this.updateOfflinePatientData(patientId, patientData, false);
    }

    const guardianSignature = await this.getSessionGuardianSignature(
      oldSessionID
    );
    if (guardianSignature) {
      await this.saveSessionGuardianSignature(
        newSessionID,
        guardianSignature
      );
      await this.removeDataFromIndexedDB(
        `offlineSessionGuardianSignature${oldSessionID}`
      );
    }

    const staffSignature = await this.getSessionStaffHandoffSignature(
      oldSessionID
    );
    if (staffSignature) {
      await this.saveSessionStaffHandoffSignature(newSessionID, staffSignature);
      await this.removeDataFromIndexedDB(
        `offlineSessionStaffHandoffSignature${oldSessionID}`
      );
    }
  }

  // Sync all offline data with the server
  public syncOfflineDataWithGroupReconciliation(): Observable<boolean> {
    return this.groupSessionService
      .reconcilePendingGroups()
      .pipe(switchMap(() => this.syncOfflineData()));
  }

  private syncOfflineData(): Observable<boolean> {
    if (this.isSyncingOfflineData) {
      return of(false);
    }

    this.isSyncingOfflineData = true;

    if (this.isInOfflineMode()) {
      this.isSyncingOfflineData = false;
      return throwError(() => new Error('Cannot sync while offline'));
    }

    // Check if authenticated
    if (!this.authService.isSignedIn()) {
      this.isSyncingOfflineData = false;
      return throwError(() => new Error('User not authenticated'));
    }

    const patientsMap = this.offlinePatients.getValue();
    if (!patientsMap) {
      this.isSyncingOfflineData = false;
      return of(false);
    }
    const syncPromises: Promise<any>[] = [];
    const syncStartedAt = Date.now();
    const syncStats = {
      dirtySessions: 0,
      targetData: 0,
      behaviorData: 0,
      progressNotes: 0,
      targetNoteMessages: 0,
      guardianSignatures: 0,
    };

    patientsMap.forEach((patientData, patientId) => {
      // Sync sessions
      patientData.sessions.forEach((session) => {
        //Check if session is finished and was updated on server
        const finishedAndSent =
          session.FinishedDate && session.FinishedHandledDate;

        // Try to save some sessions that did not save the data but were finished
        let sessionHasNonSavedData = false;
        if (
          session.TargetData?.find((x) => !x.ID) ||
          session.BehaviorData?.find((x) => !x.ID)
        ) {
          sessionHasNonSavedData = true;
          session.isDirty = true;
        }

        // Only sync dirty sessions
        if (
          (session.isDirty && session.ID && session.ID > 0) ||
          (sessionHasNonSavedData && session.ID && session.ID > 0)
        ) {
          syncStats.dirtySessions++;
          syncStats.targetData += session.TargetData?.length ?? 0;
          syncStats.behaviorData += session.BehaviorData?.length ?? 0;
          // Create a clean copy of the session without the isDirty flag
          const { isDirty, ...cleanSession } = session;

          this.dateStartedSendingOfflineData = new Date();
          const promise = this.staffApiService
            .SavePatientSession({ body: cleanSession })
            .toPromise()
            .then(async (result) => {
              // After successful sync, mark the session as clean
              const updatedPatientData = this.getOfflinePatientData(patientId);
              if (updatedPatientData && result) {
                const sessionIndex = updatedPatientData.sessions.findIndex(
                  (s) => s.ID === session.ID
                );
                if (sessionIndex >= 0) {
                  let stillDirty = false;
                  //Keep the target data from local if there is no target data on server with that same RecordedDate
                  const localTargetData = session.TargetData?.filter(
                    (x) =>
                      !result.TargetData?.some(
                        (y) =>
                          y.RecordedDate?.slice(0, 19) ===
                          x.RecordedDate?.slice(0, 19)
                      )
                  );
                  if (!result.TargetData) {
                    result.TargetData = [];
                  }
                  if (localTargetData && localTargetData.length > 0) {
                    result.TargetData = [
                      ...localTargetData,
                      ...result.TargetData,
                    ];
                    stillDirty = true;
                  }

                  //same with behavior data
                  const localBehaviorData = session.BehaviorData?.filter(
                    (x) =>
                      !result.BehaviorData?.some(
                        (y) =>
                          y.RecordedDate?.slice(0, 19) ===
                          x.RecordedDate?.slice(0, 19)
                      )
                  );
                  if (!result.BehaviorData) {
                    result.BehaviorData = [];
                  }
                  if (localBehaviorData && localBehaviorData.length > 0) {
                    result.BehaviorData = [
                      ...localBehaviorData,
                      ...result.BehaviorData,
                    ];
                    stillDirty = true;
                  }

                  let dontUpdate = false;
                  if (
                    this.dateUpdatedOfflineData &&
                    this.dateUpdatedOfflineData >
                      this.dateStartedSendingOfflineData
                  ) {
                    dontUpdate = true;
                  }

                  if (!dontUpdate) {
                    updatedPatientData.sessions[sessionIndex] = {
                      ...result,
                      isDirty: stillDirty,
                    };
                    await this.updateOfflinePatientData(
                      patientId,
                      updatedPatientData
                    );
                  }
                }
              }
              return result;
            });

          syncPromises.push(promise);
        }
      });

      // Sync progress notes if they exist
      if (patientData.progressNotes) {
        // For each session ID in the progress notes
        Object.entries(patientData.progressNotes).forEach(
          ([sessionId, progressNote]) => {
            // Check if the progress note has a dirty flag
            if ((progressNote as any).isDirty) {
              syncStats.progressNotes++;
              // Clean copy without the isDirty flag
              const cleanNote = { ...progressNote };
              delete (cleanNote as any).isDirty;

              const promise = this.staffApiService
                .SaveSessionProgressNotes({ body: cleanNote })
                .toPromise()
                .then(async (result) => {
                  // After successful sync, mark the note as clean
                  const updatedPatientData =
                    this.getOfflinePatientData(patientId);
                  if (
                    updatedPatientData &&
                    updatedPatientData.progressNotes &&
                    updatedPatientData.progressNotes[Number(sessionId)]
                  ) {
                    // Use the result from the server if available, otherwise keep existing note but mark as clean
                    if (result) {
                      updatedPatientData.progressNotes[Number(sessionId)] =
                        result;
                    } else {
                      (
                        updatedPatientData.progressNotes[
                          Number(sessionId)
                        ] as any
                      ).isDirty = false;
                    }
                    await this.updateOfflinePatientData(
                      patientId,
                      updatedPatientData
                    );
                  }
                  return result;
                });

              syncPromises.push(promise);
            }
          }
        );
      }

      if (patientData.targetNoteMessages) {
        Object.entries(patientData.targetNoteMessages).forEach(
          ([targetId, messages]) => {
            messages
              .filter(
                (message) =>
                  message.SyncStatus === 'pending' ||
                  message.SyncStatus === 'failed'
              )
              .forEach((message) => {
                syncStats.targetNoteMessages++;
                message.SyncStatus = 'syncing';

                const promise = this.staffApiService
                  .AddTargetNoteMessage({
                    body: {
                      PatientTargetID:
                        message.PatientTargetID ?? Number(targetId),
                      Message: message.Message,
                      ClientCreatedDate: message.ClientCreatedDate,
                      ClientMessageKey: message.ClientMessageKey,
                      ShouldMarkAsNew: message.ShouldMarkAsNew ?? true,
                    },
                  })
                  .toPromise()
                  .then(async (result) => {
                    const updatedPatientData =
                      this.getOfflinePatientData(patientId);
                    const updatedMessages =
                      updatedPatientData?.targetNoteMessages?.[Number(targetId)];
                    if (!updatedPatientData || !updatedMessages || !result) {
                      return result;
                    }

                    const messageIndex = updatedMessages.findIndex(
                      (offlineMessage) =>
                        offlineMessage.ClientMessageKey ===
                        message.ClientMessageKey
                    );
                    if (messageIndex >= 0) {
                      updatedMessages[messageIndex] = {
                        ...result,
                        SyncStatus: 'synced',
                        ShouldMarkAsNew: message.ShouldMarkAsNew,
                      };
                      await this.updateOfflinePatientData(
                        patientId,
                        updatedPatientData
                      );
                    }

                    return result;
                  })
                  .catch(async (error) => {
                    const updatedPatientData =
                      this.getOfflinePatientData(patientId);
                    const updatedMessages =
                      updatedPatientData?.targetNoteMessages?.[Number(targetId)];
                    const messageIndex =
                      updatedMessages?.findIndex(
                        (offlineMessage) =>
                          offlineMessage.ClientMessageKey ===
                          message.ClientMessageKey
                      ) ?? -1;
                    if (updatedPatientData && updatedMessages && messageIndex >= 0) {
                      updatedMessages[messageIndex] = {
                        ...updatedMessages[messageIndex],
                        SyncStatus: 'failed',
                      };
                      await this.updateOfflinePatientData(
                        patientId,
                        updatedPatientData
                      );
                    }

                    return Promise.reject(error);
                  });

                syncPromises.push(promise);
              });
          }
        );
      }
    });

    const promiseGuardianSignature = this.getAllGuardianSgnatureKeys()
      .then(async (keys) => {
        syncStats.guardianSignatures = keys.length;
        if (keys.length > 0) {
          // Create an array to collect all promises
          const uploadPromises = [];

          for (const key of keys) {
            const sessionId = Number(
              key.split('offlineSessionGuardianSignature')[1]
            );
            if (this.sendingGuardianSignatureKeys.includes(sessionId)) {
              continue;
            }
            this.sendingGuardianSignatureKeys.push(sessionId);

            // Create a promise for each signature upload and add to array
            const uploadPromise = this.getDataFromIndexedDB(key)
              .then((signature) => {
                if (signature) {
                  // Convert observable to promise
                  return new Promise((resolve, reject) => {
                    this.staffApiService
                      .UploadGuardianSignature({
                        sessionID: Number(sessionId),
                        body: { file: signature as Blob },
                      })
                      .subscribe(
                        (success) => {
                          console.log('Guardian signature uploaded', success);
                          this.removeDataFromIndexedDB(key);
                          this.sendingGuardianSignatureKeys =
                            this.sendingGuardianSignatureKeys.filter(
                              (key) => key !== sessionId
                            );
                          resolve(success);
                        },
                        (error) => {
                          console.error(
                            'Error uploading guardian signature',
                            error
                          );
                          this.sendingGuardianSignatureKeys =
                            this.sendingGuardianSignatureKeys.filter(
                              (key) => key !== sessionId
                            );
                          reject(error);
                        }
                      );
                  });
                }
                return Promise.resolve(); // Return resolved promise if no signature
              })
              .catch((error) => {
                console.error('Error getting guardian signature', error);
                this.sendingGuardianSignatureKeys =
                  this.sendingGuardianSignatureKeys.filter(
                    (key) => key !== Number(sessionId)
                  );
                return Promise.reject(error);
              });

            uploadPromises.push(uploadPromise);
          }

          // Wait for all upload promises to complete
          await Promise.all(uploadPromises);
          console.log('All guardian signatures processed');
        }
      })
      .catch((error) => {
        console.error('Error syncing guardian signatures', error);
      });

    syncPromises.push(promiseGuardianSignature);

    const promiseStaffSignature = this.getAllStaffSignatureKeys()
      .then(async (keys) => {
        if (keys.length > 0) {
          const uploadPromises = [];
          for (const key of keys) {
            const sessionId = Number(
              key.split('offlineSessionStaffHandoffSignature')[1]
            );
            if (this.sendingStaffSignatureKeys.includes(sessionId)) {
              continue;
            }
            this.sendingStaffSignatureKeys.push(sessionId);

            const uploadPromise = this.getDataFromIndexedDB(key)
              .then((signature) => {
                if (signature) {
                  return new Promise((resolve, reject) => {
                    this.staffApiService
                      .UploadStaffHandoffSignature({
                        sessionID: Number(sessionId),
                        body: { file: signature as Blob },
                      })
                      .subscribe(
                        (success) => {
                          console.log('Staff handoff signature uploaded', success);
                          this.removeDataFromIndexedDB(key);
                          this.sendingStaffSignatureKeys =
                            this.sendingStaffSignatureKeys.filter(
                              (sendingSessionId) => sendingSessionId !== sessionId
                            );
                          resolve(success);
                        },
                        (error) => {
                          console.error(
                            'Error uploading staff handoff signature',
                            error
                          );
                          this.sendingStaffSignatureKeys =
                            this.sendingStaffSignatureKeys.filter(
                              (sendingSessionId) => sendingSessionId !== sessionId
                            );
                          reject(error);
                        }
                      );
                  });
                }
                return Promise.resolve();
              })
              .catch((error) => {
                console.error('Error getting staff handoff signature', error);
                this.sendingStaffSignatureKeys =
                  this.sendingStaffSignatureKeys.filter(
                    (sendingSessionId) => sendingSessionId !== Number(sessionId)
                  );
                return Promise.reject(error);
              });
            uploadPromises.push(uploadPromise);
          }

          await Promise.all(uploadPromises);
          console.log('All staff handoff signatures processed');
        }
      })
      .catch((error) => {
        console.error('Error syncing staff handoff signatures', error);
      });

    syncPromises.push(promiseStaffSignature);

    return new Observable<boolean>((observer) => {
      Promise.all(syncPromises)
        .then(() => {
          console.info('Spark offline sync completed', {
            ...syncStats,
            elapsedMs: Date.now() - syncStartedAt,
          });
          observer.next(true);
          observer.complete();
          this.isSyncingOfflineData = false;
        })
        .catch((error) => {
          console.error('Error syncing offline data', {
            ...syncStats,
            elapsedMs: Date.now() - syncStartedAt,
            error,
          });
          observer.error(error);
          this.isSyncingOfflineData = false;
        });
    });
  }

  private nativeNotifyTimer: ReturnType<typeof setTimeout> | null = null;

  private notifyNativeUnsyncedChanged(): void {
    if (this.nativeNotifyTimer) {
      clearTimeout(this.nativeNotifyTimer);
    }
    this.nativeNotifyTimer = setTimeout(() => {
      try {
        const handler = (window as any).webkit?.messageHandlers?.sparkDiagnostics;
        handler?.postMessage({ type: 'unsyncedChanged' });
      } catch {
        // Native bridge is absent in a regular browser.
      }
    }, 1500);
  }

  // Generate a new PIN for offline access
  private generateAndStoreOfflinePIN(): void {
    // Generate a 6-digit PIN
    const pin = Math.floor(100000 + Math.random() * 900000).toString();
    this.setOfflinePIN(pin);

    // In a real implementation, you would also want to store this PIN on the backend
    // associated with the user, but that's beyond the scope of the local storage implementation
  }

  /**
   * Drop instructions, notes, BIP text, and similar heavy fields from the
   * in-memory + IndexedDB cache. Used when Light Load is turned on without
   * a full re-download.
   */
  public applyLightLoadInPlace(): void {
    this.lightLoad.setEnabled(true);
    this.patientDataFacade.stripCachedHeavyFields();
    const currentMap = this.offlinePatients.getValue();
    if (!currentMap || currentMap.size === 0) {
      return;
    }
    currentMap.forEach((data, patientId) => {
      this.stripHeavyOfflineFields(data);
      void this.updateOfflinePatientData(patientId, data, false);
    });
  }

  private stripHeavyOfflineFields(data: PatientOfflineData): void {
    this.lightLoad.stripTargets(data.targets);
    this.lightLoad.stripPrograms(data.programs);
    this.lightLoad.stripBehaviors(data.behaviors);
    this.lightLoad.stripPlaylists(data.playlists);
    data.patientInstructions = '';
    data.skillAcquisitionGoals = [];
    data.behaviorGoals = [];
    data.bipInterventions = [];
    data.patientBipInfo = null;
    data.behaviorBipInfos = [];
    data.behaviorFunctions = [];
  }

  // Update offline patient data and persist to IndexedDB
  private updateOfflinePatientData(
    patientId: number,
    data: PatientOfflineData,
    shouldSync: boolean = false
  ): Promise<boolean | undefined> {
    const dataPatientId = Number(data.patient?.ID);
    if (
      !Number.isFinite(dataPatientId) ||
      dataPatientId !== Number(patientId)
    ) {
      console.error('Rejected cross-patient offline cache write', {
        patientId,
        dataPatientId,
      });
      return Promise.resolve(false);
    }

    // Use shallow copy instead of deep copy to reduce memory usage
    const newData = {
      ...data,
      sparkModeKey: this.academyModeService.getModeKey(),
      isAcademyMode: this.academyModeService.isAcademyModeEnabled(),
    };

    const currentMap = this.offlinePatients.getValue();
    if (!currentMap) {
      return Promise.resolve(false);
    }
    currentMap.set(patientId, newData);

    // Update BehaviorSubject with new map reference
    this.offlinePatients.next(new Map(currentMap));

    this.dateUpdatedOfflineData = new Date();

    return this.persistPatientCatalogAndSessions(patientId, newData)
      .then(async () => {
        this.notifyNativeUnsyncedChanged();
        return true;
      })
      .catch(() => {
        return false;
      });
  }

  private persistPatientSessionsOnly(
    patientId: number,
    data: PatientOfflineData
  ): Promise<boolean | undefined> {
    const currentMap = this.offlinePatients.getValue();
    if (!currentMap) {
      return Promise.resolve(false);
    }
    currentMap.set(patientId, data);
    this.offlinePatients.next(new Map(currentMap));
    this.dateUpdatedOfflineData = new Date();

    return this.persistPatientSessions(patientId, data)
      .then(() => {
        this.notifyNativeUnsyncedChanged();
        return true;
      })
      .catch(() => {
        return false;
      });
  }

  private persistPatientCatalogAndSessions(
    patientId: number,
    data: PatientOfflineData
  ): Promise<void> {
    const catalog = {
      ...data,
      sessions: [],
      finishedSessions: [],
    };
    return this.saveEntriesToIndexedDB([
      { key: this.getPatientStorageKey(patientId), value: catalog },
      {
        key: this.getPatientSessionsStorageKey(patientId),
        value: {
          sessions: data.sessions ?? [],
          finishedSessions: data.finishedSessions ?? [],
        },
      },
    ]);
  }

  private persistPatientSessions(
    patientId: number,
    data: PatientOfflineData
  ): Promise<void> {
    return this.saveDataToIndexedDB(this.getPatientSessionsStorageKey(patientId), {
      sessions: data.sessions ?? [],
      finishedSessions: data.finishedSessions ?? [],
    });
  }

  // Remove patient data from offline storage
  public removeOfflinePatientData(patientId: number): void {
    const currentMap = this.offlinePatients.getValue();
    if (currentMap && currentMap.has(patientId)) {
      currentMap.delete(patientId);
      this.offlinePatients.next(new Map(currentMap));

      // Remove from IndexedDB using new format
      this.removeDataFromIndexedDB(this.getPatientStorageKey(patientId));
      this.removeDataFromIndexedDB(this.getPatientSessionsStorageKey(patientId));
      this.removeDataFromIndexedDB(this.getLegacyPatientStorageKey(patientId));
    }
  }

  public removeBulkOfflinePatientData(patientIds: number[]): void {
    const currentMap = this.offlinePatients.getValue();
    if (currentMap) {
      patientIds.forEach((patientId) => {
        if (currentMap.has(patientId)) {
          currentMap.delete(patientId);
          // Remove from IndexedDB using new format
          this.removeDataFromIndexedDB(this.getPatientStorageKey(patientId));
          this.removeDataFromIndexedDB(this.getPatientSessionsStorageKey(patientId));
          this.removeDataFromIndexedDB(this.getLegacyPatientStorageKey(patientId));
        }
      });
      this.offlinePatients.next(new Map(currentMap));
    }
  }

  // Save offline session progress notes - with lazy loading support
  public saveOfflineSessionProgressNotes(
    patientId: number,
    sessionId: number
  ): Observable<SessionProgressNoteModel | null> {
    if (this.isInOfflineMode()) {
      // If offline, return existing progress notes if available
      const patientData = this.getOfflinePatientData(patientId);
      if (
        patientData &&
        patientData.progressNotes &&
        patientData.progressNotes[sessionId]
      ) {
        return of(patientData.progressNotes[sessionId]);
      }
      return of(null);
    }

    // NOTE: 2025/#14330-note-summary-from-spark-has-wrong-data-on-it - humberto - 2025-07-24
    // I commented out the next lines regarding cached data because the logic to that generates new progress notes depending on session changes is in the GetSessionProgressNotes API.
    // If we use cached data instead of fetching from the server, we will not be able to get latest progress notes data.

    // Check if we already have the progress note cached
    //const patientData = this.getOfflinePatientData(patientId);
    //if (patientData && patientData.progressNotes && patientData.progressNotes[sessionId]) {
    //  return of(patientData.progressNotes[sessionId]);
    //}

    // If online and not cached, fetch from server
    return this.staffApiService
      .GetSessionProgressNotes({ patientSessionID: sessionId })
      .pipe(
        tap((progressNote) => {
          // Save the progress note to offline storage
          const patientData = this.getOfflinePatientData(patientId);
          if (patientData) {
            if (!patientData.progressNotes) {
              patientData.progressNotes = {};
            }
            patientData.progressNotes[sessionId] = progressNote;
            // Don't sync when fetching progress notes to avoid memory spikes during initialization
            this.updateOfflinePatientData(patientId, patientData, false);
          }
        }),
        catchError((error) => {
          console.error('Error fetching session progress notes', error);
          return of(null);
        })
      );
  }

  // Preload progress notes for multiple sessions (optional optimization)
  public preloadProgressNotes(
    patientId: number,
    sessionIds: number[]
  ): Observable<boolean> {
    if (this.isInOfflineMode() || sessionIds.length === 0) {
      return of(true);
    }

    // Load progress notes in small batches to avoid memory spikes
    const batchSize = 3; // Load 3 at a time to balance performance and memory
    const batches: number[][] = [];

    for (let i = 0; i < sessionIds.length; i += batchSize) {
      batches.push(sessionIds.slice(i, i + batchSize));
    }

    // Process batches sequentially
    return batches.reduce((acc, batch) => {
      return acc.pipe(
        switchMap(() => {
          const batchRequests = batch.map((sessionId) =>
            this.saveOfflineSessionProgressNotes(patientId, sessionId).pipe(
              catchError(() => of(null))
            )
          );
          return forkJoin(batchRequests);
        }),
        map(() => true)
      );
    }, of(true));
  }

  // Get offline session progress notes
  public getOfflineSessionProgressNotes(
    patientId: number,
    sessionId: number
  ): SessionProgressNoteModel | null {
    const patientData = this.getOfflinePatientData(patientId);
    if (
      patientData &&
      patientData.progressNotes &&
      patientData.progressNotes[sessionId]
    ) {
      return patientData.progressNotes[sessionId];
    }
    return null;
  }

  // Save modified progress notes
  public saveModifiedProgressNotes(
    patientId: number,
    sessionId: number,
    progressNote: SessionProgressNoteModel
  ): Observable<boolean> {
    const patientData = this.getOfflinePatientData(patientId);
    if (!patientData) {
      return throwError(() => new Error('Patient data not found offline'));
    }

    // Make sure the progressNotes object exists
    if (!patientData.progressNotes) {
      patientData.progressNotes = {};
    }

    // Add the dirty flag to track that this note has been modified
    const modifiedNote = {
      ...progressNote,
      isDirty: true,
    };

    // Save the progress note
    patientData.progressNotes[sessionId] = modifiedNote;
    // Don't sync automatically to avoid memory spikes, sync will happen when needed
    this.updateOfflinePatientData(patientId, patientData, false);

    return of(true);
  }

  /**
   * Handles the transition from offline to online mode
   * This ensures that offline data is synced to the server before
   * fetching new data from the server
   * @private
   */
  private handleOnlineTransition(): void {
    console.log('Handling transition from offline to online mode');
    // Check if there's data to sync
    const patientsMap = this.offlinePatients.getValue();
    if (!patientsMap || patientsMap.size === 0) {
      return;
    }

    let hasDirtyData = false;

    // Check if there are any dirty sessions, target notes, or progress notes that need to be synced
    patientsMap.forEach((patientData, patientId) => {
      // Check for dirty sessions
      const hasDirtySessions = patientData.sessions.some(
        (session) => session.isDirty
      );

      // Check for dirty progress notes
      let hasDirtyProgressNotes = false;
      if (patientData.progressNotes) {
        hasDirtyProgressNotes = Object.values(patientData.progressNotes).some(
          (note) => (note as any).isDirty
        );
      }

      const hasPendingTargetNoteMessages =
        this.hasPendingTargetNoteMessages(patientData);

      if (
        hasDirtySessions ||
        hasDirtyProgressNotes ||
        hasPendingTargetNoteMessages
      ) {
        console.log(`Patient ${patientId} has dirty data that needs syncing`);
        hasDirtyData = true;
      }
    });

    if (hasDirtyData) {
      // If there's dirty data, sync it before transitioning to online mode
      console.log('Syncing offline data before transitioning to online mode');
      this.syncOfflineDataWithGroupReconciliation().subscribe({
        next: () => {
          console.log(
            'Successfully synced offline data during online transition'
          );
        },
        error: (error) => {
          console.error(
            'Error syncing offline data during online transition:',
            error
          );
        },
      });
    } else {
      // No dirty data to sync, just set offline mode to false
      console.log('No dirty data to sync');
    }
  }

  // Save the guardian signature for a session
  public saveSessionGuardianSignature(
    sessionId: number,
    guardianSignature: Blob
  ): void {
    this.saveDataToIndexedDB(
      'offlineSessionGuardianSignature' + sessionId,
      guardianSignature
    );
  }

  public getSessionGuardianSignature(sessionId: number): Promise<Blob | null> {
    return this.getDataFromIndexedDB(
      'offlineSessionGuardianSignature' + sessionId
    );
  }

  public saveSessionStaffHandoffSignature(
    sessionId: number,
    signature: Blob
  ): void {
    this.saveDataToIndexedDB(
      `offlineSessionStaffHandoffSignature${sessionId}`,
      signature
    );
  }

  public getOfflineTargetNoteMessages(
    patientId: number,
    targetId: number
  ): OfflineTargetNoteMessage[] {
    const patientData = this.getOfflinePatientData(patientId);
    return [
      ...(patientData?.targetNoteMessages?.[targetId] ?? []),
    ].sort((a, b) => {
      const aDate = new Date(a.ClientCreatedDate ?? a.CreatedDate ?? 0).getTime();
      const bDate = new Date(b.ClientCreatedDate ?? b.CreatedDate ?? 0).getTime();
      return aDate - bDate;
    });
  }

  public saveOfflineTargetNoteMessage(
    patientId: number,
    targetId: number,
    message: string,
    shouldMarkAsNew: boolean
  ): Observable<OfflineTargetNoteMessage> {
    const patientData = this.getOfflinePatientData(patientId);
    if (!patientData) {
      return throwError(() => new Error('Patient data not found offline'));
    }

    if (!patientData.targetNoteMessages) {
      patientData.targetNoteMessages = {};
    }

    const now = new Date().toISOString();
    const userInfo = this.authService.userInfo;
    const targetNoteMessage: OfflineTargetNoteMessage = {
      ID: -Date.now(),
      PatientTargetID: targetId,
      Message: message,
      CreatedByUserID: userInfo?.ID ?? null,
      CreatedByUserFullName:
        userInfo?.Fullnane ?? userInfo?.UserName ?? 'Current user',
      CreatedDate: now,
      ClientCreatedDate: now,
      ClientMessageKey: this.createGuid(),
      IsCurrentUser: true,
      SyncStatus: 'pending',
      ShouldMarkAsNew: shouldMarkAsNew,
    };

    patientData.targetNoteMessages[targetId] = [
      ...(patientData.targetNoteMessages[targetId] ?? []),
      targetNoteMessage,
    ];

    this.updateOfflinePatientData(patientId, patientData, false);
    return of(targetNoteMessage);
  }

  private createGuid(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (value) => {
      const random = (Math.random() * 16) | 0;
      const replacement = value === 'x' ? random : (random & 0x3) | 0x8;
      return replacement.toString(16);
    });
  }

  public getSessionStaffHandoffSignature(sessionId: number): Promise<Blob | null> {
    return this.getDataFromIndexedDB(
      `offlineSessionStaffHandoffSignature${sessionId}`
    );
  }

  // Load offline patients data from IndexedDB - optimized approach
  private loadOfflinePatientsData(): void {
    if (!this.db) {
      this.offlinePatients.next(new Map());
      return;
    }

    const transaction = this.db.transaction(['offlineData'], 'readonly');
    const store = transaction.objectStore('offlineData');
    const patientMap = new Map<number, PatientOfflineData>();

    // Get all keys to find patient data
    const getAllKeysRequest = store.getAllKeys();

    getAllKeysRequest.onsuccess = () => {
      const keys = getAllKeysRequest.result as string[];
      const currentMode = this.academyModeService.getModeKey();
      const patientKeys = keys.filter((key) =>
        this.isPatientStorageKeyForMode(key, currentMode) &&
        !key.endsWith('_sessions')
      );

      if (patientKeys.length === 0) {
        // Legacy patient cache did not include academy/standard mode and should
        // only be used by standard mode. Academy mode must fetch its own cache.
        if (currentMode === 'standard') {
          this.loadLegacyPatientsData(patientMap);
        } else {
          this.offlinePatients.next(patientMap);
        }
        return;
      }

      let loadedCount = 0;

      // Load each patient data individually to avoid large memory spikes
      patientKeys.forEach((key) => {
        const getRequest = store.get(key);
        getRequest.onsuccess = () => {
          const patientData = getRequest.result?.value as PatientOfflineData;
          if (patientData) {
            const patientId = this.getPatientIdFromStorageKey(key);
            if (patientId != null && this.isOfflineDataForCurrentMode(patientData)) {
              const sessionsRequest = store.get(
                this.getPatientSessionsStorageKey(patientId, currentMode)
              );
              sessionsRequest.onsuccess = () => {
                const sessionsSlice = sessionsRequest.result?.value as
                  | {
                      sessions?: PatientOfflineData['sessions'];
                      finishedSessions?: PatientOfflineData['finishedSessions'];
                    }
                  | undefined;
                if (sessionsSlice?.sessions) {
                  patientData.sessions = sessionsSlice.sessions;
                  patientData.finishedSessions =
                    sessionsSlice.finishedSessions ??
                    patientData.finishedSessions ??
                    [];
                }
                patientMap.set(patientId, patientData);
                loadedCount++;
                if (loadedCount === patientKeys.length) {
                  this.offlinePatients.next(patientMap);
                }
              };
              sessionsRequest.onerror = () => {
                patientMap.set(patientId, patientData);
                loadedCount++;
                if (loadedCount === patientKeys.length) {
                  this.offlinePatients.next(patientMap);
                }
              };
              return;
            }
          }

          loadedCount++;
          if (loadedCount === patientKeys.length) {
            this.offlinePatients.next(patientMap);
          }
        };

        getRequest.onerror = () => {
          console.error(`Error loading patient data for key: ${key}`);
          loadedCount++;
          if (loadedCount === patientKeys.length) {
            this.offlinePatients.next(patientMap);
          }
        };
      });
    };

    getAllKeysRequest.onerror = () => {
      console.error('Error getting patient keys from IndexedDB');
      this.offlinePatients.next(new Map());
    };
  }

  // Handle legacy data format for backward compatibility
  private loadLegacyPatientsData(
    patientMap: Map<number, PatientOfflineData>
  ): void {
    this.getDataFromIndexedDB('offlinePatients')
      .then((offlinePatientsData: string | null) => {
        if (offlinePatientsData) {
          try {
            const parsed = JSON.parse(offlinePatientsData);

            // Migrate to new format and load data
            for (const key in parsed) {
              if (parsed.hasOwnProperty(key)) {
                const patientId = Number(key);
                const patientData = {
                  ...(parsed[key] as PatientOfflineData),
                  sparkModeKey: 'standard' as SparkModeKey,
                  isAcademyMode: false,
                };
                patientMap.set(patientId, patientData);

                // Save in new format
                this.saveDataToIndexedDB(
                  this.getPatientStorageKey(patientId, 'standard'),
                  patientData
                );
              }
            }

            // Remove legacy data
            this.removeDataFromIndexedDB('offlinePatients');
          } catch (error) {
            console.error('Error parsing legacy offline patients data', error);
          }
        }

        this.offlinePatients.next(patientMap);

      })
      .catch((error) => {
        console.error('Error loading legacy patient data:', error);
        this.offlinePatients.next(new Map());
      });
  }

  private getPatientStorageKey(
    patientId: number,
    modeKey: SparkModeKey = this.academyModeService.getModeKey()
  ): string {
    return `patient_${modeKey}_${patientId}`;
  }

  private getPatientSessionsStorageKey(
    patientId: number,
    modeKey: SparkModeKey = this.academyModeService.getModeKey()
  ): string {
    return `${this.getPatientStorageKey(patientId, modeKey)}_sessions`;
  }

  private getLegacyPatientStorageKey(patientId: number): string {
    return `patient_${patientId}`;
  }

  private isPatientStorageKeyForMode(
    key: string,
    modeKey: SparkModeKey
  ): boolean {
    if (key.startsWith(`patient_${modeKey}_`)) {
      return true;
    }

    return modeKey === 'standard' && /^patient_\d+$/.test(key);
  }

  private getPatientIdFromStorageKey(key: string): number | null {
    const modeScopedMatch = key.match(/^patient_(?:standard|academy|isd_iop)_(\d+)$/);
    if (modeScopedMatch) {
      return Number(modeScopedMatch[1]);
    }

    const legacyMatch = key.match(/^patient_(\d+)$/);
    if (legacyMatch) {
      return Number(legacyMatch[1]);
    }

    return null;
  }

  private isOfflineDataForCurrentMode(patientData: PatientOfflineData): boolean {
    const currentMode = this.academyModeService.getModeKey();
    const storedMode =
      patientData.sparkModeKey ??
      (patientData.isAcademyMode ? 'academy' : 'standard');
    return storedMode === currentMode;
  }
}

// Interface for offline patient data
export interface PatientOfflineData {
  sparkModeKey?: SparkModeKey;
  isAcademyMode?: boolean;
  patient: PatientModel;
  behaviors: PatientBehaviorModel[];
  targets: PatientTargetModel[];
  sessions: (PatientSessionModel & { isDirty?: boolean })[];
  finishedSessions: (PatientSessionModel & { isDirty?: boolean })[];
  behaviorLocations: any[];
  behaviorConsequences: any[];
  behaviorResponses: any[];
  behaviorSeverities: any[];
  behaviorAntecedents: any[];
  behaviorReplacements: any[];
  skillAreas: SkillAreaModel[];
  programs: PatientProgramModel[];
  playlists: PatientPlaylistModel[];
  promptSchedules: PromptScheduleModel[];
  genericPromptCodes: PromptCodeModel[];
  skillAcquisitionGoals: SkillAcquisitionGoalModel[];
  behaviorGoals: BehaviorGoalModel[];
  reminders: PatientReminderModel[];
  progressNotes: { [sessionId: number]: SessionProgressNoteModel };
  targetNoteMessages?: { [targetId: number]: OfflineTargetNoteMessage[] };
  patientInstructions: string;
  bipInterventions: string[];
  // New: cached BIP data for offline
  patientBipInfo: PatientBipInfoModel | null;
  behaviorBipInfos: BehaviorBipInfoModel[];
  behaviorFunctions: BehaviorFunctionModel[];
  // Cached for offline session finalize / handoff
  guardians: GuardianModel[];
  activeStaffMembers: DataTakerStaffDataModel[];
  programsDeltaCursor?: string | null;
  targetsDeltaCursor?: string | null;
  lastSyncTime: Date;
}

export interface OfflineTargetNoteMessage extends TargetNoteMessageModel {
  SyncStatus?: 'pending' | 'syncing' | 'failed' | 'synced';
  ShouldMarkAsNew?: boolean;
}
